import { useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useContext } from 'react'

import { FileItem } from 'api/ApiTypes'
import { getCustomerApiClient } from 'api/CustomerApiClient'
import { CUSTOMER_QUERY_IDS } from 'api/Queries'
import AuthContext from 'context/AuthContext'
import { withDummyMock } from 'dummy/static'
import { FILE_SIGNATURE_EXPIRATION } from 'utils/FileUtils'

type GetDocumentsResponse = {
  hasMore: boolean
  results: FileItem[]
}

type GetDocumentsReturnValue = {
  data: GetDocumentsResponse
  isLoadingMore: boolean
  status: UseInfiniteQueryResult<FileItem[], unknown>['status']
  fetchMore: () => void
}

type Props = {
  size?: number
  entityId?: string | null
  enabled?: boolean
  fileTypes?: [string, ...string[]]
}

export const useGetDocuments = ({
  size = 10,
  entityId,
  enabled = true,
  fileTypes
}: Props = {}): GetDocumentsReturnValue => {
  const { data: authData } = useContext(AuthContext)

  const query = useInfiniteQuery(
    [CUSTOMER_QUERY_IDS.GET_DOCUMENTS, entityId, size],
    (params) =>
      withDummyMock(
        ({ pageParam = 0 }) =>
          getCustomerApiClient().getAllFiles({
            from: pageParam,
            size,
            entity_ids: entityId ? [entityId] : undefined,
            file_types: fileTypes
          }),
        CUSTOMER_QUERY_IDS.GET_DOCUMENTS
      )(params).then((res: AxiosResponse) => res.data),
    {
      select: (result) => result,
      enabled: authData.initialized && enabled,
      refetchInterval: FILE_SIGNATURE_EXPIRATION - 1000 * 60,
      getNextPageParam: (lastPage, allPages) => {
        // Returns where to start the next page 'from' or undefined if there are no more pages
        if (lastPage?.hits > allPages?.length * size) {
          return allPages.length * size
        }

        return undefined
      }
    }
  )

  const {
    data: responseData,
    status,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = query

  const results = responseData?.pages.reduce(
    (acc, page) => [...acc, ...page.results],
    []
  ) as FileItem[]

  return {
    data: { hasMore: hasNextPage, results },
    fetchMore: fetchNextPage,
    isLoadingMore: isFetchingNextPage,
    status
  }
}
